import React, { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { resolveImage, uploadImage } from "@/lib/media";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";

// `upload` overrides the default `uploadImage(file, businessId)` for a surface whose upload route differs
// (e.g. the admin console uploading into a city it is not signed in to) - same widget, same toasts.
export function ImageUpload({ value, onChange, businessId, testId = "image-upload", label, upload }) {
  const { t } = useApp();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const data = await (upload ? upload(file) : uploadImage(file, businessId));
      onChange(data.url);
      toast.success(t("media.uploaded"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("media.failed")));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label || t("media.upload")}</p>
      <div className="flex items-center gap-4">
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-border bg-muted">
          {value ? (
            <img src={resolveImage(value)} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
          ) : (
            <div className="grid h-full w-full place-items-center text-muted-foreground">
              <ImagePlus size={18} />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/*" hidden onChange={pick} data-testid={`${testId}-input`} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full"
            disabled={busy}
            data-testid={`${testId}-btn`}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ImagePlus size={14} className="mr-2" />}
            {busy ? t("media.uploading") : t("media.upload")}
          </Button>
          {value && (
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground"
              data-testid={`${testId}-remove`}
              onClick={() => onChange("")}
            >
              <X size={12} /> {t("media.remove")}
            </button>
          )}
          <p className="max-w-xs text-xs text-muted-foreground">{t("media.hint")}</p>
        </div>
      </div>
    </div>
  );
}
